// Library methods
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import DeviceCard from "../../shared/DeviceCard/DeviceCard";
import DevicesHeader from "../../shared/DevicesHeader/DevicesHeader";
import ExpandDevicesModal from "./ExpandDevicesModal/ExpandDevicesModal";

// Contexts
import { useDevicesContext } from "src/contexts/devices/DevicesContext";

const PatientDevices = () => {
  const { t } = useTranslation();
  const { devices, isLoadingDevices } = useDevicesContext();

  const [expandDevicesModal, setExpandDevicesModal] = useState(false);
  const [isActive, setIsActive] = useState("all");

  const sortedDevices = useMemo(
    () => devices?.sort((a, b) => a?.alias?.localeCompare(b?.alias)),
    [devices]
  );

  const filteredDevices = useMemo(() => {
    switch (isActive) {
      case "linked":
        return sortedDevices?.filter((device) => device?.associatedExam);
      case "unlinked":
        return sortedDevices?.filter((device) => !device?.associatedExam);
      default:
        return sortedDevices;
    }
  }, [isActive, sortedDevices]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: "100%",
        gap: "16px",
      }}
    >
      {!isLoadingDevices && devices && devices?.length > 0 && (
        <DevicesHeader
          showButtons={Boolean(devices && devices?.length > 1)}
          isActive={isActive}
          setIsActive={setIsActive}
          setExpandDevicesModal={() => setExpandDevicesModal(true)}
        />
      )}
      {isLoadingDevices ? (
        <Skeleton
          variant="rounded"
          width="100%"
          sx={{
            height: "250px",
            borderRadius: "8px",
          }}
        />
      ) : (
        <Box
          gap="8px"
          display="flex"
          sx={{
            px: "0px",
            alignItems: "center",
            justifyContent: "flex-start",
            overflowY: "hidden",
            overflowX: "auto",
            flexWrap: "nowrap",
            pb: filteredDevices && filteredDevices?.length > 0 ? "8px" : "0px",
          }}
        >
          {filteredDevices && filteredDevices?.length > 0 ? (
            filteredDevices?.map((device) => {
              return <DeviceCard key={device.id} device={device} />;
            })
          ) : (
            <Box
              width="100%"
              height="246px"
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Typography fontSize="16px" fontWeight={600}>
                {t("No Devices Found.")}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <ExpandDevicesModal
        open={expandDevicesModal}
        onClose={() => setExpandDevicesModal(false)}
      >
        <Box
          sx={{
            display: "flex",
            gap: "16px",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {sortedDevices?.map((device) => (
            <DeviceCard key={device.id} device={device} />
          ))}
        </Box>
      </ExpandDevicesModal>
    </Box>
  );
};

export default PatientDevices;
